/**
 * F2 — Prayer requests: pure state and visibility logic.
 *
 * ACTUAL PRIVACY is enforced by permissions engine in sync (a
 * private request never reaches SQLite of unauthorized device) and
 * write-path (`owner_stamp`: only requester or `prayer:manage`
 * edit). These helpers are client MIRROR of same semantics: gating
 * UI actions and defensively filtering local data with same rules.
 */

export type PrayerStatus = "praying" | "answered";

export interface PrayerRequestLike {
  id: string;
  activity_id?: string | null;
  requester_user_id: string | null;
  requester_name?: string | null;
  content?: string | null;
  /** SQLite replicates booleans as 0/1; server emits as boolean. */
  is_private: boolean | number | null;
  status: string | null;
  answered_at?: string | null;
  created_at?: string | null;
  deleted_at?: string | null;
}

/** Viewer context: active account + whether having scope `prayer:manage`. */
export interface PrayerViewer {
  userId: string | null | undefined;
  canModerate: boolean;
}

/** Normalizes privacy flag (server boolean / SQLite 0-1). */
export function isPrivatePrayer(row: Pick<PrayerRequestLike, "is_private">): boolean {
  return row.is_private === true || row.is_private === 1;
}

export function isAnswered(row: Pick<PrayerRequestLike, "status">): boolean {
  return row.status === "answered";
}

/**
 * Can viewer VIEW this request? Public -> yes; private -> only
 * petitioner or moderator (`prayer:manage`). Same rule as streams.
 */
export function canSeePrayerRequest(row: PrayerRequestLike, viewer: PrayerViewer): boolean {
  if (!isPrivatePrayer(row)) {
    return true;
  }
  if (viewer.canModerate) {
    return true;
  }
  return Boolean(viewer.userId) && row.requester_user_id === viewer.userId;
}

/**
 * Can EDIT (status/content/trash)? Owner or moderator — mirror
 * exacto del write-path (`owner_stamp`: write ajeno exige manage).
 */
export function canEditPrayerRequest(row: PrayerRequestLike, viewer: PrayerViewer): boolean {
  if (viewer.canModerate) {
    return true;
  }
  return Boolean(viewer.userId) && row.requester_user_id === viewer.userId;
}

/**
 * Visible requests of activity: outside trash, visible to viewer,
 * and in arrival order (answered at the end).
 */
export function visiblePrayerRequests<T extends PrayerRequestLike>(
  rows: readonly T[],
  activityId: string,
  viewer: PrayerViewer,
): T[] {
  return rows
    .filter((row) => row.activity_id === activityId)
    .filter((row) => !row.deleted_at)
    .filter((row) => canSeePrayerRequest(row, viewer))
    .toSorted((a, b) => {
      const answered = Number(isAnswered(a)) - Number(isAnswered(b));
      if (answered !== 0) {
        return answered;
      }
      return (a.created_at ?? "").localeCompare(b.created_at ?? "");
    });
}

/** State transition praying <-> answered (with timestamp). */
export function togglePrayerStatus(
  row: Pick<PrayerRequestLike, "status">,
  nowIso: string,
): { status: PrayerStatus; answered_at: string | null } {
  return isAnswered(row)
    ? { status: "praying", answered_at: null }
    : { status: "answered", answered_at: nowIso };
}
